/**
 * CTE / AM Thermal Expansion Calculator
 * Anisotropic CTE calculator with material library, 3D STL analysis, and CTE curves.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import './CTECalculator.css';

import {
  DEFAULT_MATERIALS,
  interpolateCTE,
  buildAngleToEuler,
  buildCycleTimeline, precomputeCycleFrames,
  niceAxis, meshExtents,
} from './cte-utils';

import { ColorBar, BuildAngleGizmo, CycleSTLViewer, CadModelProvider, useCadModelUpload } from './cte-shared';
import MouldCompensation from './MouldCompensation';
import CTEAIPanel from './CTEAIPanel';
import CTEBuildPlanner from './CTEBuildPlanner';
import { generateCycleSimPDF } from './cte-cycle-sim-pdf';
import { RelatedGuide } from '../../components/ui';

// ─── CYCLE SIMULATION ─────────────────────────────────────────────────────────
function CycleSimulation({ materials, positions, orient, buildAngle }) {
  const [selId, setSelId] = useState(materials[0]?.id || '');
  const partMaterials = materials.filter(m => m.cureTemp !== null);
  const [partMatId, setPartMatId] = useState(partMaterials[0]?.id || '');
  const [cycleSteps, setCycleSteps] = useState([
    { type: 'ramp', targetTemp: 180, rate: 2 },
    { type: 'hold', temp: 180, duration: 60 },
    { type: 'ramp', targetTemp: 23, rate: 1 },
  ]);
  const [startTemp, setStartTemp] = useState(23);
  const [frames, setFrames] = useState(null);
  const [globalMaxDisp, setGlobalMaxDisp] = useState(0);
  const [chartData, setChartData] = useState(null);
  const [peakInfo, setPeakInfo] = useState(null);
  const [cureInfo, setCureInfo] = useState(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [cycleDefScale, setCycleDefScale] = useState(1);
  const [cycleAIData, setCycleAIData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const playRef = useRef(null);
  const viewerRef = useRef(null);

  const mat = materials.find(m => m.id === selId);
  const partMat = materials.find(m => m.id === partMatId);

  const updateStep = (idx, field, value) => {
    setCycleSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addStep = (type) => {
    if (type === 'ramp') {
      setCycleSteps(prev => [...prev, { type: 'ramp', targetTemp: 23, rate: 1 }]);
    } else {
      setCycleSteps(prev => [...prev, { type: 'hold', temp: 120, duration: 30 }]);
    }
  };

  const removeStep = (idx) => {
    setCycleSteps(prev => prev.filter((_, i) => i !== idx));
  };

  const moveStep = (idx, dir) => {
    setCycleSteps(prev => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const runCycleSim = () => {
    if (!mat || !positions) return;
    setPlaying(false);
    setFrameIndex(0);
    setAiAnalysis(null);
    const timeline = buildCycleTimeline(startTemp, cycleSteps, 0.5);
    const result = precomputeCycleFrames(mat, orient, positions, timeline);
    setFrames(result.frames);
    setGlobalMaxDisp(result.globalMaxDisp);

    let peakIdx = 0;
    const data = result.frames.map((f, i) => {
      if (f.maxDispMM > result.frames[peakIdx].maxDispMM) peakIdx = i;
      return { time: +f.time.toFixed(1), temp: +f.temp.toFixed(1), disp: +f.maxDispMM.toFixed(4) };
    });
    setChartData(data);
    setPeakInfo({
      index: peakIdx,
      time: result.frames[peakIdx].time,
      temp: result.frames[peakIdx].temp,
      disp: result.frames[peakIdx].maxDispMM,
    });

    let cureData = null;
    if (partMat && partMat.cureTemp !== null) {
      const cureIdx = result.frames.findIndex(f => f.temp >= partMat.cureTemp);
      if (cureIdx >= 0) {
        cureData = {
          index: cureIdx,
          time: result.frames[cureIdx].time,
          temp: result.frames[cureIdx].temp,
          disp: result.frames[cureIdx].maxDispMM,
          partCureTemp: partMat.cureTemp,
          partName: partMat.name,
        };
        setCureInfo(cureData);
      } else {
        setCureInfo(null);
      }
    } else {
      setCureInfo(null);
    }

    const aiPayload = {
      materialName: mat.name,
      cureTemp: mat.cureTemp,
      peakDisp: result.frames[peakIdx].maxDispMM.toFixed(4),
      peakTemp: result.frames[peakIdx].temp.toFixed(1),
      peakTime: result.frames[peakIdx].time.toFixed(1),
      totalDuration: result.frames[result.frames.length - 1].time.toFixed(1),
      startTemp,
      cycleSteps,
      buildAngle: buildAngle || 0,
    };
    if (cureData) {
      aiPayload.partMaterialName = cureData.partName;
      aiPayload.partCureTemp = cureData.partCureTemp;
      aiPayload.curePointDisp = cureData.disp.toFixed(4);
      aiPayload.extraMovement = (result.frames[peakIdx].maxDispMM - cureData.disp).toFixed(4);
    }
    setCycleAIData(aiPayload);
  };

  useEffect(() => {
    if (!playing || !frames) return;
    const totalFrames = frames.length;
    playRef.current = setInterval(() => {
      setFrameIndex(prev => {
        if (prev >= totalFrames - 1) {
          setPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(playRef.current);
  }, [playing, frames]);

  const currentFrame = frames ? frames[Math.min(frameIndex, frames.length - 1)] : null;
  const totalDuration = frames ? frames[frames.length - 1].time : 0;

  const handleExportPDF = useCallback(() => {
    if (!peakInfo || !mat) return;

    let viewerImage = null;
    if (viewerRef?.current) {
      try {
        const canvas = viewerRef.current.querySelector('canvas');
        if (canvas) viewerImage = canvas.toDataURL('image/png');
      } catch (_) { /* tainted canvas */ }
    }

    const pdfData = {
      mouldMaterial: mat.name,
      mouldCureTemp: mat.cureTemp,
      partMaterial: partMat?.name || null,
      partCureTemp: partMat?.cureTemp || null,
      startTemp,
      cycleSteps,
      peakDisp: peakInfo.disp.toFixed(4),
      peakTemp: peakInfo.temp.toFixed(1),
      peakTime: peakInfo.time.toFixed(1),
      totalDuration: totalDuration.toFixed(1),
      cureInfo: cureInfo ? {
        partName: cureInfo.partName,
        partCureTemp: cureInfo.partCureTemp,
        disp: cureInfo.disp.toFixed(4),
        time: cureInfo.time.toFixed(1),
        extraMovement: (peakInfo.disp - cureInfo.disp).toFixed(4),
      } : null,
      chartData,
      cureTime: cureInfo ? +cureInfo.time.toFixed(1) : null,
      viewerImage,
      aiAnalysis,
    };

    generateCycleSimPDF(pdfData);
  }, [peakInfo, mat, partMat, startTemp, cycleSteps, totalDuration, cureInfo, chartData, aiAnalysis]);

  const cycleSummary = useMemo(() => {
    if (cycleSteps.length === 0) return null;
    let temp = startTemp;
    let totalTime = 0;
    let maxTemp = startTemp;
    for (const step of cycleSteps) {
      if (step.type === 'ramp') {
        const delta = Math.abs(step.targetTemp - temp);
        totalTime += delta / (step.rate || 1);
        temp = step.targetTemp;
      } else {
        totalTime += step.duration || 0;
      }
      if (temp > maxTemp) maxTemp = temp;
    }
    return { maxTemp: maxTemp.toFixed(0), estDuration: totalTime.toFixed(0), steps: cycleSteps.length };
  }, [cycleSteps, startTemp]);

  return (
    <div style={{ maxWidth: 1000 }}>
      <div className="cte-card">
        <div className="cte-card-header">
          <span>Cycle Setup</span>
          {cycleSummary && (
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
              {cycleSummary.steps} steps | Peak {cycleSummary.maxTemp} deg C | ~{cycleSummary.estDuration} min
            </span>
          )}
        </div>
        <div className="cte-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="cte-form-group">
              <label>Mould Material</label>
              <select value={selId} onChange={e => setSelId(e.target.value)}>
                <optgroup label="Mould / Tool Materials">
                  {materials.filter(m => m.category === 'mould').map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </optgroup>
                <optgroup label="Layup / Part Materials">
                  {materials.filter(m => m.category === 'layup').map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </optgroup>
              </select>
            </div>
            <div className="cte-form-group">
              <label>Part Material</label>
              <select value={partMatId} onChange={e => setPartMatId(e.target.value)}>
                <optgroup label="Layup / Part Materials">
                  {materials.filter(m => m.category === 'layup' && m.cureTemp !== null).map(m => (
                    <option key={m.id} value={m.id}>{m.name} -- Cure {m.cureTemp} deg C</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="cte-form-group">
              <label>Start Temperature (deg C)</label>
              <input type="number" value={startTemp} onChange={e => setStartTemp(+e.target.value)} />
            </div>
          </div>

          <div className="cte-cycle-steps">
            {cycleSteps.map((step, idx) => (
              <div key={idx} className="cte-cycle-step-row">
                <span className="cte-cycle-step-num">{idx + 1}</span>
                <span className={`cte-cycle-step-type ${step.type === 'ramp' ? 'cte-step-ramp' : 'cte-step-hold'}`}>
                  {step.type === 'ramp' ? 'Ramp' : 'Hold'}
                </span>
                {step.type === 'ramp' ? (
                  <>
                    <div className="cte-form-group">
                      <label>Target (deg C)</label>
                      <input type="number" value={step.targetTemp} onChange={e => updateStep(idx, 'targetTemp', +e.target.value)} />
                    </div>
                    <div className="cte-form-group">
                      <label>Rate (deg C/min)</label>
                      <input type="number" value={step.rate} step="0.5" min="0.1" onChange={e => updateStep(idx, 'rate', +e.target.value)} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="cte-form-group">
                      <label>Temp (deg C)</label>
                      <input type="number" value={step.temp} onChange={e => updateStep(idx, 'temp', +e.target.value)} />
                    </div>
                    <div className="cte-form-group">
                      <label>Duration (min)</label>
                      <input type="number" value={step.duration} min="1" onChange={e => updateStep(idx, 'duration', +e.target.value)} />
                    </div>
                  </>
                )}
                <div className="cte-step-actions">
                  <button className="cte-btn cte-btn-ghost cte-btn-icon" onClick={() => moveStep(idx, -1)} disabled={idx === 0} title="Move up">^</button>
                  <button className="cte-btn cte-btn-ghost cte-btn-icon" onClick={() => moveStep(idx, 1)} disabled={idx === cycleSteps.length - 1} title="Move down">v</button>
                  <button className="cte-btn cte-btn-ghost cte-btn-icon" onClick={() => removeStep(idx)} title="Remove step">x</button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={() => addStep('ramp')}>+ Ramp</button>
            <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={() => addStep('hold')}>+ Hold</button>
            <button className="cte-btn cte-btn-primary" style={{ marginLeft: 'auto' }} onClick={runCycleSim} disabled={!mat || cycleSteps.length === 0}>
              Run Simulation
            </button>
          </div>
        </div>
      </div>

      {frames && currentFrame && (
        <>
          <div className="cte-stats-grid" style={{ marginTop: 16 }}>
            {[
              { label: 'Peak Distortion', value: peakInfo.disp.toFixed(4), unit: 'mm' },
              { label: 'Peak Temp', value: peakInfo.temp.toFixed(1), unit: 'deg C' },
              { label: 'Peak Time', value: peakInfo.time.toFixed(1), unit: 'min' },
              { label: 'Total Duration', value: totalDuration.toFixed(1), unit: 'min' },
            ].map(({ label, value, unit }) => (
              <div key={label} className="cte-stat-box">
                <div className="cte-stat-label">{label}</div>
                <div className="cte-stat-value">{value}</div>
                <div className="cte-stat-unit">{unit}</div>
              </div>
            ))}
          </div>

          {cureInfo && (
            <div className="cte-card">
              <div className="cte-card-header">
                <span>Cure Point -- When Part Goes Rigid</span>
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
                  {cureInfo.partName} -- Cure {cureInfo.partCureTemp} deg C
                </span>
              </div>
              <div className="cte-card-body">
                <div className="cte-stats-grid">
                  {[
                    { label: 'Distortion at Cure', value: cureInfo.disp.toFixed(4), unit: 'mm' },
                    { label: 'Part Cures At', value: cureInfo.time.toFixed(1), unit: 'min' },
                    { label: 'Cure Temp', value: cureInfo.partCureTemp.toFixed(0), unit: 'deg C' },
                    { label: 'Extra Movement After Cure', value: (peakInfo.disp - cureInfo.disp).toFixed(4), unit: 'mm' },
                  ].map(({ label, value, unit }) => (
                    <div key={label} className="cte-stat-box">
                      <div className="cte-stat-label">{label}</div>
                      <div className="cte-stat-value">{value}</div>
                      <div className="cte-stat-unit">{unit}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="cte-card" ref={viewerRef}>
              <div className="cte-card-header">
                <span>3D Distortion Viewer</span>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', textTransform: 'none', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-secondary)' }}>
                  <input type="checkbox" checked={showOriginal} onChange={e => setShowOriginal(e.target.checked)} style={{ width: 12, height: 12 }} />
                  Wireframe
                </label>
              </div>
              <div className="cte-card-body">
                <CycleSTLViewer
                  positions={positions}
                  frames={frames}
                  frameIndex={frameIndex}
                  globalMaxDisp={globalMaxDisp}
                  showOriginal={showOriginal}
                  orient={orient}
                  defScale={cycleDefScale}
                />
                <ColorBar maxVal={globalMaxDisp} unit="mm" />
                <div className="cte-timeline-controls" style={{ marginTop: 8 }}>
                  <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', whiteSpace: 'nowrap' }}>Scale</span>
                  <input type="range" className="cte-timeline-slider" min={1} max={10} value={cycleDefScale}
                    onChange={e => setCycleDefScale(+e.target.value)} />
                  <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {cycleDefScale === 1 ? 'true scale' : `${cycleDefScale}× shown larger`}
                  </span>
                </div>
              </div>
            </div>

            <div className="cte-card">
              <div className="cte-card-header">Temperature & Distortion</div>
              <div className="cte-card-body">
                {chartData && chartData.length > 1 && (() => {
                  const xAxis = niceAxis(chartData[0].time, chartData[chartData.length - 1].time, { includeZero: true, target: 6 });
                  const temps = chartData.map((d) => d.temp);
                  const disps = chartData.map((d) => d.disp);
                  const yTemp = niceAxis(Math.min(...temps), Math.max(...temps), { target: 5 });
                  const yDisp = niceAxis(0, Math.max(0, ...disps), { includeZero: true, target: 5 });
                  return (
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 28 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" type="number" scale="linear"
                        domain={[xAxis.min, xAxis.max]} ticks={xAxis.ticks} interval={0}
                        tickFormatter={formatAxisTick(xAxis.step)}
                        label={{ value: 'Time (min)', position: 'insideBottom', offset: -16, fill: 'var(--rf-text-tertiary)', fontSize: 12 }}
                        tick={{ fontSize: 11, fill: 'var(--rf-text-tertiary)' }} />
                      <YAxis yAxisId="temp" width={52}
                        domain={[yTemp.min, yTemp.max]} ticks={yTemp.ticks} interval={0}
                        tickFormatter={formatAxisTick(yTemp.step)}
                        label={{ value: 'Temperature (°C)', angle: -90, position: 'insideLeft', offset: 0, fill: 'var(--rf-text-tertiary)', fontSize: 12 }}
                        tick={{ fontSize: 11, fill: 'var(--rf-text-tertiary)' }} />
                      <YAxis yAxisId="disp" orientation="right" width={56}
                        domain={[yDisp.min, yDisp.max]} ticks={yDisp.ticks} interval={0}
                        tickFormatter={formatAxisTick(yDisp.step)}
                        label={{ value: 'Distortion (mm)', angle: 90, position: 'insideRight', offset: 4, fill: 'var(--rf-text-tertiary)', fontSize: 12 }}
                        tick={{ fontSize: 11, fill: 'var(--rf-text-tertiary)' }} />
                      <Tooltip
                        contentStyle={{ background: 'var(--rf-bg-elevated)', border: '1px solid var(--rf-border-color)', borderRadius: 8 }}
                        labelStyle={{ color: 'var(--rf-text-secondary)', fontSize: 11 }}
                        itemStyle={{ fontSize: 11 }}
                        labelFormatter={(t) => `t = ${t} min`} />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                      {currentFrame && (
                        <ReferenceLine x={+currentFrame.time.toFixed(1)} yAxisId="temp" stroke="rgba(var(--rf-veil-rgb), 0.3)" strokeDasharray="3 3" />
                      )}
                      {cureInfo && (
                        <ReferenceLine x={+cureInfo.time.toFixed(1)} yAxisId="temp" stroke="var(--rf-success)" strokeDasharray="6 3"
                          label={{ value: 'Part Cures', position: 'top', fill: '#10b981', fontSize: 10 }} />
                      )}
                      {peakInfo && (
                        <ReferenceLine x={+peakInfo.time.toFixed(1)} yAxisId="temp" stroke="var(--rf-danger)" strokeDasharray="6 3"
                          label={{ value: 'Peak', position: 'top', fill: '#ef4444', fontSize: 10 }} />
                      )}
                      <Line yAxisId="temp" type="linear" dataKey="temp" stroke="var(--rf-warning)" dot={false} strokeWidth={2} name="Temperature" />
                      <Line yAxisId="disp" type="linear" dataKey="disp" stroke="var(--rf-primary)" dot={false} strokeWidth={2} name="Max Distortion" />
                    </LineChart>
                  </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="cte-timeline-controls" style={{ marginTop: 16 }}>
            <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={() => {
              if (frameIndex >= frames.length - 1) setFrameIndex(0);
              setPlaying(!playing);
            }}>
              {playing ? 'Pause' : 'Play'}
            </button>
            <input
              type="range"
              className="cte-timeline-slider"
              min={0}
              max={frames.length - 1}
              value={frameIndex}
              onChange={e => { setPlaying(false); setFrameIndex(+e.target.value); }}
            />
            <div className="cte-timeline-readout">
              <span>{currentFrame.time.toFixed(1)} min</span>
              <span>{currentFrame.temp.toFixed(1)} deg C</span>
              <span>{currentFrame.maxDispMM.toFixed(4)} mm</span>
            </div>
          </div>

          {cycleAIData && (
            <CTEAIPanel
              analysisData={cycleAIData}
              label="Cycle Simulation -- AI Thermal Process Review"
              endpoint="/api/cte/ai/cycle-analysis"
              onFeedback={setAiAnalysis}
              autoRun
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="cte-btn cte-btn-primary" onClick={handleExportPDF}>
              Export PDF Report
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── CAD ANALYSIS TAB ─────────────────────────────────────────────────────────
function CADAnalysis({ materials }) {
  const [buildAngle, setBuildAngle] = useState(0);
  const [buildAzimuth, setBuildAzimuth] = useState(0);
  const orient = useMemo(() => buildAngleToEuler(buildAngle, buildAzimuth), [buildAngle, buildAzimuth]);
  const { model, loadFile } = useCadModelUpload();
  const stlPositions = model.positions;
  const fileName = model.fileName;
  const error = model.error;
  const meshWarning = model.warning;
  const loadingFile = model.loading;
  const plateSize = useMemo(() => meshExtents(stlPositions), [stlPositions]);

  const handleBuildAngleChange = useCallback((angle, azimuth) => {
    setBuildAngle(angle);
    setBuildAzimuth(azimuth);
  }, []);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    await loadFile(file);
  };

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="cte-card">
          <div className="cte-card-header">
            <span>AM Print Orientation</span>
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
              Set how the part sits on the build plate
            </span>
          </div>
          <div className="cte-card-body">
            <BuildAngleGizmo buildAngle={buildAngle} buildAzimuth={buildAzimuth} onAngleChange={handleBuildAngleChange} size={plateSize} />
            <div className="cte-angle-controls">
              <div className="cte-form-group" style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Build Angle</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={0} max={90} value={buildAngle}
                      onChange={e => handleBuildAngleChange(Math.max(0, Math.min(90, +e.target.value || 0)), buildAzimuth)}
                      style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                    <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                  </span>
                </label>
                <input type="range" min="0" max="90" step="1" value={buildAngle}
                  onChange={e => handleBuildAngleChange(+e.target.value, buildAzimuth)} style={{ width: '100%' }} />
              </div>
              <div className="cte-form-group" style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Rotation</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={0} max={359} value={buildAzimuth}
                      onChange={e => handleBuildAngleChange(buildAngle, Math.max(0, Math.min(359, +e.target.value || 0)))}
                      style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                    <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                  </span>
                </label>
                <input type="range" min="0" max="359" step="1" value={buildAzimuth}
                  onChange={e => handleBuildAngleChange(buildAngle, +e.target.value)} style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        </div>

        <div className="cte-card">
          <div className="cte-card-header">3D Model Input</div>
        <div className="cte-card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input ref={fileRef} type="file" accept=".stl,.obj,.step,.stp" onChange={handleFile} style={{ display: 'none' }} />
            <button className="cte-btn cte-btn-primary" onClick={() => fileRef.current.click()} disabled={loadingFile}>
              {loadingFile ? 'Parsing...' : 'Upload STL / OBJ / STEP'}
            </button>
            {fileName && (
              <span style={{ fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-secondary)' }}>
                {fileName}
                {stlPositions && <span style={{ color: 'var(--rf-success)', marginLeft: 10 }}>
                  {(stlPositions.length/9).toLocaleString()} triangles
                </span>}
              </span>
            )}
          </div>
          {error && <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)', marginTop: 10 }}>{error}</div>}
          {meshWarning && (
            <div className="cte-warn-box" style={{ marginTop: 10 }}>
              <span>Note:</span>
              <span>{meshWarning}</span>
            </div>
          )}
          {!stlPositions && !loadingFile && (
            <div className="cte-upload-area">
              <div className="cte-upload-icon">
                <i className="fas fa-cube"></i>
              </div>
              Upload an STL, OBJ, or STEP file of your mould tool.<br />
              The thermal distortion heat map will be computed for each vertex.<br />
              The file stays loaded when you change tabs.
            </div>
          )}
        </div>
        </div>
      </div>

      {loadingFile && (
        <div className="cte-loading-overlay">
          <div className="cte-loading-modal">
            <div className="cte-loading-spinner" />
            <div className="cte-loading-title">Parsing CAD File</div>
            <div className="cte-loading-sub">{fileName || 'Loading...'}</div>
            <div className="cte-loading-hint">STEP files require the geometry engine to load. This can take a moment.</div>
          </div>
        </div>
      )}

      {stlPositions && (
        <div style={{ marginTop: 24 }}>
          <CycleSimulation materials={materials} positions={stlPositions} orient={orient} buildAngle={buildAngle} />
        </div>
      )}
    </div>
  );
}

// ─── MATERIAL EDITOR ──────────────────────────────────────────────────────────
function MaterialEditor({ material, onSave, onDelete, onNew }) {
  const [m, setM] = useState(material);
  useEffect(() => { setM(material); }, [material]);
  const setField = (f, v) => setM(prev => ({ ...prev, [f]: v }));
  const setDP = (idx, field, val) => {
    const pts = m.dataPoints.map((p, i) => i === idx ? { ...p, [field]: val === '' ? '' : parseFloat(val) || 0 } : p);
    setField('dataPoints', pts);
  };
  const addDP = () => {
    const lastT = m.dataPoints.length ? m.dataPoints[m.dataPoints.length - 1].temp + 20 : 23;
    setField('dataPoints', [...m.dataPoints, { temp: lastT, ax: 0, ay: 0, az: 0 }]);
  };
  const removeDP = (idx) => setField('dataPoints', m.dataPoints.filter((_, i) => i !== idx));
  const sorted = [...m.dataPoints.map((p, i) => ({ ...p, _idx: i }))].sort((a, b) => a.temp - b.temp);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="cte-section-label">Material Editor</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={onNew}>+ New</button>
          {material.id !== 'NEW' && <button className="cte-btn cte-btn-danger cte-btn-sm" onClick={() => onDelete(material.id)}>Delete</button>}
          <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={() => onSave(m)}>Save</button>
        </div>
      </div>
      {m.notes && m.notes.startsWith('APPROXIMATION') && (
        <div className="cte-warn-box" style={{ marginBottom: 16 }}>
          <span>Approximation:</span>
          <span>
            This material has limited data from a single ASTM E831 measurement.
            The temperature-dependent curve and Tg transition are engineering estimates, not measured values.
          </span>
        </div>
      )}
      <div className="cte-card">
        <div className="cte-card-header">Identity</div>
        <div className="cte-card-body">
          <div className="cte-form-row">
            <div className="cte-form-group grow">
              <label>Material Name</label>
              <input type="text" value={m.name} onChange={e => setField('name', e.target.value)} />
            </div>
            <div className="cte-form-group">
              <label>Version</label>
              <input type="text" value={m.version} onChange={e => setField('version', e.target.value)} style={{ width: 80 }} />
            </div>
          </div>
          <div className="cte-form-row">
            <div className="cte-form-group">
              <label>Type</label>
              <select value={m.category} onChange={e => setField('category', e.target.value)}>
                <option value="mould">Mould / Tool</option>
                <option value="layup">Layup / Part</option>
              </select>
            </div>
          </div>
          <div className="cte-form-row">
            <div className="cte-form-group">
              <label>Cure Temp (deg C)</label>
              <input type="number" value={m.cureTemp ?? ''} placeholder="N/A"
                onChange={e => setField('cureTemp', e.target.value === '' ? null : parseFloat(e.target.value))} style={{ width: 110 }} />
            </div>
            <div className="cte-form-group">
              <label>Post-Cure CTE Scale</label>
              <input type="number" value={m.postCureScale} step="0.1"
                onChange={e => setField('postCureScale', parseFloat(e.target.value) || 1)} style={{ width: 110 }} />
            </div>
          </div>
          <div className="cte-form-group">
            <label>Notes</label>
            <textarea value={m.notes} onChange={e => setField('notes', e.target.value)} />
          </div>
        </div>
      </div>
      <div className="cte-card">
        <div className="cte-card-header">
          <span>CTE Data Points</span>
          <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginRight: 'auto', marginLeft: 12 }}>
            For AM parts: Z = build direction (through layers)
          </div>
          <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={addDP}>+ Add Row</button>
        </div>
        <div className="cte-card-body" style={{ overflowX: 'auto' }}>
          <table className="cte-dp-table">
            <thead><tr>
              <th>Temp (deg C)</th>
              <th>aX in-plane (10⁻⁶/K)</th>
              <th>aY in-plane (10⁻⁶/K)</th>
              <th>aZ build dir (10⁻⁶/K)</th>
              <th></th>
            </tr></thead>
            <tbody>
              {sorted.map(pt => (
                <tr key={pt._idx}>
                  <td><input type="number" value={pt.temp} onChange={e => setDP(pt._idx, 'temp', e.target.value)} /></td>
                  <td><input type="number" value={pt.ax} step="0.1" onChange={e => setDP(pt._idx, 'ax', e.target.value)} /></td>
                  <td><input type="number" value={pt.ay} step="0.1" onChange={e => setDP(pt._idx, 'ay', e.target.value)} /></td>
                  <td><input type="number" value={pt.az} step="0.1" onChange={e => setDP(pt._idx, 'az', e.target.value)} className="cte-z-input" /></td>
                  <td><button className="cte-btn cte-btn-ghost cte-btn-icon" onClick={() => removeDP(pt._idx)}>x</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatAxisTick(step) {
  return (v) => {
    if (!Number.isFinite(v)) return '';
    if (Math.abs(step) >= 1) return String(Math.round(v));
    const digits = Math.abs(step) >= 0.1 ? 1 : 2;
    return Number(v).toFixed(digits);
  };
}

// ─── CTE CURVES ───────────────────────────────────────────────────────────────
function CTECurves({ materials }) {
  const [selId, setSelId] = useState(materials[0]?.id || '');
  const mat = materials.find(m => m.id === selId);
  const chartData = (() => {
    if (!mat || mat.dataPoints.length < 2) return [];
    const sorted = [...mat.dataPoints].sort((a, b) => a.temp - b.temp);
    const minT = sorted[0].temp - 10, maxT = sorted[sorted.length - 1].temp + 20;
    return Array.from({ length: 201 }, (_, i) => {
      const T = minT + i * (maxT - minT) / 200;
      return {
        temp: +T.toFixed(1),
        'aX': +interpolateCTE(mat.dataPoints, T, 'ax', mat.cureTemp, mat.postCureScale).toFixed(3),
        'aY': +interpolateCTE(mat.dataPoints, T, 'ay', mat.cureTemp, mat.postCureScale).toFixed(3),
        'aZ (build)': +interpolateCTE(mat.dataPoints, T, 'az', mat.cureTemp, mat.postCureScale).toFixed(3),
      };
    });
  })();

  return (
    <div>
      <div className="cte-card">
        <div className="cte-card-header">Material</div>
        <div className="cte-card-body">
          <div className="cte-form-group" style={{ maxWidth: 400 }}>
            <label>Select Material</label>
            <select value={selId} onChange={e => setSelId(e.target.value)}>
              {materials.map(m => <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>)}
            </select>
          </div>
        </div>
      </div>
      {mat && (
        <div className="cte-card">
          <div className="cte-card-header">
            CTE vs Temperature — {mat.name}
            {mat.cureTemp && <span style={{ marginLeft: 10, color: 'var(--rf-warning)', fontSize: 'var(--rf-font-size-xs)' }}>Tc = {mat.cureTemp} deg C</span>}
          </div>
          <div className="cte-card-body">
            {chartData.length > 1 && (() => {
              const xAxis = niceAxis(chartData[0].temp, chartData[chartData.length - 1].temp, { target: 6 });
              const ys = chartData.flatMap((d) => [d.aX, d.aY, d['aZ (build)']]);
              const yAxis = niceAxis(Math.min(...ys), Math.max(...ys), { includeZero: true, target: 5 });
              return (
            <ResponsiveContainer width="100%" height={380}>
              <LineChart data={chartData} margin={{ top: 12, right: 24, left: 8, bottom: 28 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="temp" type="number" scale="linear"
                  domain={[xAxis.min, xAxis.max]} ticks={xAxis.ticks} interval={0}
                  tickFormatter={formatAxisTick(xAxis.step)}
                  label={{ value: 'Temperature (°C)', position: 'insideBottom', offset: -16, fill: 'var(--rf-text-tertiary)', fontSize: 12 }}
                  tick={{ fontSize: 11, fill: 'var(--rf-text-tertiary)' }} />
                <YAxis width={64}
                  domain={[yAxis.min, yAxis.max]} ticks={yAxis.ticks} interval={0}
                  tickFormatter={formatAxisTick(yAxis.step)}
                  label={{ value: 'CTE (10⁻⁶/K)', angle: -90, position: 'insideLeft', offset: 0, fill: 'var(--rf-text-tertiary)', fontSize: 12 }}
                  tick={{ fontSize: 11, fill: 'var(--rf-text-tertiary)' }} />
                <Tooltip
                  contentStyle={{ background: 'var(--rf-bg-elevated)', border: '1px solid var(--rf-border-color)', borderRadius: 8 }}
                  labelStyle={{ color: 'var(--rf-text-secondary)', fontSize: 11 }}
                  itemStyle={{ fontSize: 11 }}
                  formatter={(v) => [`${v} × 10⁻⁶/K`]}
                  labelFormatter={(t) => `T = ${t} °C`} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                {mat.cureTemp && <ReferenceLine x={mat.cureTemp} stroke="var(--rf-warning)" strokeDasharray="6 3"
                  label={{ value: `Tc ${mat.cureTemp} °C`, position: 'top', fill: '#f59e0b', fontSize: 10 }} />}
                <Line type="linear" dataKey="aX" stroke="var(--rf-info)" dot={false} strokeWidth={2} />
                <Line type="linear" dataKey="aY" stroke="var(--rf-primary)" dot={false} strokeWidth={2} />
                <Line type="linear" dataKey="aZ (build)" stroke="var(--rf-success)" dot={false} strokeWidth={2.5} />
              </LineChart>
            </ResponsiveContainer>
              );
            })()}
            {mat.notes && mat.notes.startsWith('APPROXIMATION') && (
              <div className="cte-warn-box" style={{ marginTop: 14 }}>
                <span>Approximation:</span>
                <span>
                  This material has limited data from a single ASTM E831 measurement.
                  The temperature-dependent curve shape and Tg transition are engineering estimates, not measured values.
                  Use for preliminary analysis only — validate with actual multi-temperature CTE testing.
                </span>
              </div>
            )}
            <div className="cte-info-box" style={{ marginTop: 14 }}>
              For AM parts, <strong>aZ (build direction)</strong> is typically 10-50x higher than in-plane values.
              This drives most of the warp and spring-back behaviour in printed mould tools.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9);

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchMaterials() {
  const res = await fetch('/api/cte/materials', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load materials');
  return res.json();
}

async function persistMaterials(mats) {
  const res = await fetch('/api/cte/materials', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(mats),
  });
  if (!res.ok) throw new Error('Failed to save materials');
  return res.json();
}

const CTECalculator = () => {
  const [tab, setTab] = useState('library');
  const [materials, setMaterials] = useState(DEFAULT_MATERIALS);
  const [selId, setSelId] = useState(DEFAULT_MATERIALS[0].id);
  const [loaded, setLoaded] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    fetchMaterials()
      .then((mats) => {
        if (Array.isArray(mats) && mats.length > 0) {
          setMaterials(mats);
          setSelId(mats[0].id);
        }
      })
      .catch(() => { /* use defaults if API unavailable */ })
      .finally(() => setLoaded(true));
  }, []);

  const persist = useCallback((mats) => {
    persistMaterials(mats)
      .then(() => {
        setSaveMsg('Saved');
        setTimeout(() => setSaveMsg(''), 2000);
      })
      .catch(() => setSaveMsg('Save failed'));
  }, []);

  const saveMaterial = (m) => {
    const isNew = m.id === 'NEW' || !materials.find(x => x.id === m.id);
    const finalM = isNew ? { ...m, id: uid() } : m;
    const updated = isNew ? [...materials.filter(x => x.id !== 'NEW'), finalM] : materials.map(x => x.id === finalM.id ? finalM : x);
    setMaterials(updated);
    setSelId(finalM.id);
    persist(updated);
  };

  const deleteMaterial = (id) => {
    const updated = materials.filter(m => m.id !== id);
    setMaterials(updated);
    setSelId(updated[0]?.id || '');
    persist(updated);
  };

  const newMaterial = () => {
    const blank = {
      id: 'NEW', name: 'New AM Material', version: '1.0', category: 'mould', cureTemp: null, postCureScale: 2.0,
      notes: 'Z = build direction (through layers)',
      dataPoints: [{ temp: 23, ax: 2.5, ay: 2.5, az: 45.0 }],
    };
    setSelId('NEW');
    setMaterials(prev => prev.find(m => m.id === 'NEW') ? prev : [...prev, blank]);
  };

  const selectedMat = materials.find(m => m.id === selId) || materials[0];
  const showSidebar = (tab === 'library' || tab === 'curves');
  const activeMaterials = materials.filter(m => m.id !== 'NEW');

  if (!loaded) {
    return (
      <div className="cte-calculator">
        <div style={{ padding: 40, color: 'var(--rf-text-muted)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="cte-calculator">
      <div className="cte-container">
        <div className="cte-page-header">
          <div>
            <h1>CTE Calculator</h1>
            <div className="cte-page-header-sub">
              AM | Anisotropic | Non-Linear | Print Orientation | 3D Heat Map
            </div>
            <RelatedGuide
              title="Related guide"
              courseHref="/field-ops-course.html?id=pe320-operator"
              manualHref="/field-ops-manual.html?set=apollo&course=pe320-operator"
            >
              For PE320 print process and first-part setup, use the operator course and the Apollo cell manual.
            </RelatedGuide>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {saveMsg && <span className="cte-save-msg">{saveMsg}</span>}
            <span style={{ color: 'var(--rf-text-muted)', fontSize: 'var(--rf-font-size-sm)' }}>
              {activeMaterials.length} materials
            </span>
          </div>
        </div>

        <div className="cte-tabs">
          {[
            { id: 'library', label: 'Material Library' },
            { id: 'curves', label: 'CTE Curves' },
            { id: 'planner', label: 'Build Planner' },
            { id: 'cad', label: 'Thermal Distortion' },
            { id: 'compensation', label: 'Mould Compensation' },
          ].map(t => (
            <button key={t.id} className={`cte-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <CadModelProvider>
        <div className="cte-main">
          {showSidebar && (
            <div className="cte-sidebar">
              <div className="cte-sidebar-header">
                <span>Materials</span>
                <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={newMaterial}>+</button>
              </div>
              <div className="cte-sidebar-group-label">Mould / Tool Materials</div>
              {materials.filter(m => m.category === 'mould').map(m => (
                <div key={m.id} className={`cte-mat-item ${m.id === selId ? 'selected' : ''}`} onClick={() => setSelId(m.id)}>
                  <div className="cte-mat-name">{m.name}</div>
                  <div className="cte-mat-meta">v{m.version}{m.cureTemp !== null ? ` | Tg ${m.cureTemp} deg C` : ''}</div>
                </div>
              ))}
              <div className="cte-sidebar-group-label">Layup / Part Materials</div>
              {materials.filter(m => m.category === 'layup').map(m => (
                <div key={m.id} className={`cte-mat-item ${m.id === selId ? 'selected' : ''}`} onClick={() => setSelId(m.id)}>
                  <div className="cte-mat-name">{m.name}</div>
                  <div className="cte-mat-meta">v{m.version}{m.cureTemp !== null ? ` | Cure ${m.cureTemp} deg C` : ''}</div>
                </div>
              ))}
            </div>
          )}
          <div className="cte-content">
            {tab === 'library' && selectedMat && (
              <MaterialEditor key={selectedMat.id} material={selectedMat} onSave={saveMaterial} onDelete={deleteMaterial} onNew={newMaterial} />
            )}
            {tab === 'planner' && <CTEBuildPlanner materials={activeMaterials} />}
            {tab === 'cad' && <CADAnalysis materials={activeMaterials} />}
            {tab === 'compensation' && <MouldCompensation materials={activeMaterials} />}
            {tab === 'curves' && <CTECurves materials={materials.filter(m => m.id !== 'NEW' && m.dataPoints.length >= 2)} />}
          </div>
        </div>
        </CadModelProvider>
      </div>
      <div className="cte-disclaimer-footer">
        Rapid Fusion accepts no responsibility for the accuracy of results produced by this tool.
        All outputs are provided as a computational aid only and do not constitute engineering advice.
        A qualified engineer must review and verify all results and compensated geometry before manufacturing.
      </div>
    </div>
  );
};

export default CTECalculator;
